// An article the queue never finished stays "ongoing" forever in Directus, so the
// UI treats a generation with no sign of life for this long as failed.
// Worker allows 10 min per article (MAX_PROCESSING_TIME); the extra 5 min
// covers queue pickup so a still-running generation is not shown as failed.
const ONGOING_TIMEOUT_MS = 15 * 60 * 1000;

const normalizeStatus = (value) => String(value || '').trim().toLowerCase();
const parseDebugPayload = (payload) => {
  if (!payload) return null;
  if (typeof payload === 'object') return payload;
  if (typeof payload !== 'string') return null;
  try {
    return JSON.parse(payload);
  } catch (_) {
    return null;
  }
};

const toTimestamp = (value) => {
  if (!value) return null;
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? null : parsed;
};

// Last sign of life: when the worker picked the article up, else the last write, else creation.
const getLastProgressAt = (article = {}) => toTimestamp(
  article.processing_started_at
  ?? article.processingStartedAt
  ?? article.date_updated
  ?? article.dateUpdated
  ?? article.date_created
  ?? article.dateCreated
);

const isOngoingArticleState = (article = {}) => {
  const status = normalizeStatus(article.status);
  const queueStatus = normalizeStatus(article.queue_status ?? article.queueStatus);
  return status === 'ongoing' || queueStatus === 'queued' || queueStatus === 'processing';
};

export const isFailedArticleState = (article = {}) => {
  const status = normalizeStatus(article.status);
  const queueStatus = normalizeStatus(article.queue_status ?? article.queueStatus);
  return status === 'failed'
    || status === 'error'
    || queueStatus === 'failed'
    || queueStatus === 'error';
};

export const isStaleOngoingArticle = (article = {}, now = Date.now()) => {
  if (isFailedArticleState(article) || !isOngoingArticleState(article)) return false;

  const lastProgressAt = getLastProgressAt(article);
  if (lastProgressAt === null) return false;

  return now - lastProgressAt >= ONGOING_TIMEOUT_MS;
};

export const getDisplayArticleStatus = (article = {}, now = Date.now()) => {
  const status = normalizeStatus(article.status);

  if (isFailedArticleState(article)) return 'failed';
  if (isOngoingArticleState(article)) {
    return isStaleOngoingArticle(article, now) ? 'failed' : 'ongoing';
  }

  return status || 'draft';
};

export const getArticleFailureReason = (article = {}, now = Date.now()) => {
  if (isStaleOngoingArticle(article, now)) {
    return 'Generowanie przekroczylo limit 15 minut. Kliknij, aby sprobowac ponownie.';
  }
  if (!isFailedArticleState(article)) return null;

  const explicitReason = String(
    article?.failure_reason
    || article?.error_message
    || article?.error
    || ''
  ).trim();
  if (explicitReason) return explicitReason;

  const debugPayload = parseDebugPayload(article?.debug);
  const generationError = String(debugPayload?.generation_error || '').trim();
  if (generationError) return generationError;

  const sourcePrefetch = debugPayload?.source_prefetch || {};
  const gateOutcome = String(sourcePrefetch?.gate_outcome || '').trim().toLowerCase();
  // Empty prefetch is an intentional continue path, not the failure cause.
  if (gateOutcome === 'degraded_continue') {
    return 'Generowanie artykulu nie powiodlo sie. Kliknij, aby sprobowac ponownie.';
  }

  const metrics = sourcePrefetch?.metrics || {};
  const selectedSourcesCount = Number(
    metrics?.chosen_total
    ?? sourcePrefetch?.chosen_sources?.length
    ?? 0
  );
  const externalSourcesCount = Number(sourcePrefetch?.external_sources_count ?? 0);
  const externalToolEvidenceCount = Number(sourcePrefetch?.external_tool_evidence_count ?? 0);

  if (selectedSourcesCount === 0 && externalSourcesCount === 0) {
    return `Brak zweryfikowanych zrodel (prefetch: selected=${selectedSourcesCount}, external=${externalSourcesCount}, evidence=${externalToolEvidenceCount}).`;
  }

  if (selectedSourcesCount === 0) {
    return `Prefetch odrzucil wszystkie zrodla (external=${externalSourcesCount}, evidence=${externalToolEvidenceCount}).`;
  }

  return 'Generowanie artykulu nie powiodlo sie. Kliknij, aby sprobowac ponownie.';
};
